'use client'

import { useTranslations } from 'next-intl'
import React, { Suspense, useId, useState } from 'react'

import { LocaleLink } from '@/components/LocaleLink'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Menu } from 'lucide-react'

import { ModoEscuroToggleSheet } from '@/providers/Theme/ModoEscuroToggle'
import { LanguageFlagToggleSheet } from '@/modules/languages/components/LanguageFlagMenu.client'

import type { ResolvedNavTopButton } from '@/Header/utilities/resolveHeaderNavigation'
import type { HeaderLanguageData } from './HeaderActions'

import type { CategoryRailNavItem } from '@/Header/utilities/resolveHeaderNavigation'

import { HeaderSearch } from './HeaderSearch'

type Props = {
  categoryItems: CategoryRailNavItem[]
  /** Até 5 itens do CMS (menu superior); no topo da folha antes do menu principal. */
  superiorNavItems: CategoryRailNavItem[]
  login: ResolvedNavTopButton
  signup: ResolvedNavTopButton
  showSheetSearch: boolean
  showSheetTheme: boolean
  language?: HeaderLanguageData
}

export function HeaderMobile({
  categoryItems,
  superiorNavItems,
  login,
  signup,
  showSheetSearch,
  showSheetTheme,
  language,
}: Props) {
  const t = useTranslations('Header')
  const showLanguage = Boolean(language?.enabled && language.languages.length > 0)
  const mobileMenuId = useId()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // UX: fechar a folha ao clicar num link de navegação. Em PT os links usam soft-nav (Next <Link>) e
  // o Sheet não desmonta entre páginas, então sem isto ficaria aberto sobre a página nova. Delegação
  // por clique (em vez de efeito de rota) cobre todos os <a href> de uma vez — nav, login e signup —
  // e NÃO dispara nos toggles de tema/moeda/idioma (são <button>). (Em /es,/en o hard-nav já fecha.)
  const closeOnLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a[href]')) setMobileMenuOpen(false)
  }

  // Navegação hierárquica (estilo Business Insider): item COM filhos vira seção colapsável (accordion,
  // pai só EXPANDE, não navega — a página do pai fica no link «Ver tudo»); item SEM filhos vira link
  // simples grande, sem chevron. Ordem do CMS preservada. `LocaleLink` mantém o locale ativo.
  const renderNavItem = (item: CategoryRailNavItem) => {
    const hasChildren = (item.children?.length ?? 0) > 0
    // «Ver tudo em X» só quando o PAI tem página PRÓPRIA de verdade. Pai que é só agrupador (sem URL no
    // CMS) cai no fallback `/` (home) — «Ver tudo em Notícias» apontaria pra home, o que não faz sentido;
    // nesse caso não renderiza o link (o pai só expande o accordion). `#` = placeholder, também exclui.
    const hrefReal = Boolean(item.href) && item.href !== '#' && item.href !== '/'
    if (!hasChildren) {
      return (
        <LocaleLink
          key={`${item.href}-${item.label}`}
          href={item.href}
          className="block border-b border-dotted border-border py-4 text-lg font-semibold text-foreground hover:text-primary"
        >
          {item.label}
        </LocaleLink>
      )
    }
    return (
      <AccordionItem
        key={`${item.href}-${item.label}`}
        value={`${item.href}-${item.label}`}
        className="border-b border-dotted border-border"
      >
        <AccordionTrigger className="py-4 text-lg font-semibold text-foreground hover:no-underline">
          {item.label}
        </AccordionTrigger>
        <AccordionContent className="pb-2">
          <div className="flex flex-col">
            {hrefReal ? (
              <LocaleLink
                href={item.href}
                className="py-2.5 pl-1 text-base font-medium text-primary hover:underline"
              >
                {t('seeAllIn', { label: item.label })}
              </LocaleLink>
            ) : null}
            {item.children!.map((sub) => (
              <LocaleLink
                key={`${item.href}-${sub.href}-${sub.label}`}
                href={sub.href}
                className="py-2.5 pl-1 text-base font-medium text-muted-foreground hover:text-foreground"
              >
                {sub.label}
              </LocaleLink>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    )
  }

  return (
    <div className="lg:hidden" suppressHydrationWarning>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="clear"
            className="size-9 shrink-0"
            aria-label={t('openMenu')}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuId}
          >
            <Menu className="size-5 stroke-foreground" aria-hidden />
          </Button>
        </SheetTrigger>
        <SheetContent
          id={mobileMenuId}
          side="right"
          className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-sm"
          aria-label={t('navMenu')}
          onClick={closeOnLinkClick}
        >
          <SheetHeader>
            <SheetTitle>{t('menu')}</SheetTitle>
          </SheetHeader>
          {showSheetSearch ? (
            <Suspense fallback={<div className="h-9 rounded-md bg-muted/50" />}>
              <HeaderSearch placeholder={t('searchShort')} />
            </Suspense>
          ) : null}
          <nav aria-label={t('mobileNav')}>
            {/* Um único Accordion (single, uma seção aberta por vez) com superiorNavItems e depois
                categoryItems na ordem do CMS; itens sem filhos são LocaleLinks avulsos (Radix renderiza
                filhos não-Item normalmente). */}
            <Accordion type="single" collapsible className="w-full">
              {superiorNavItems.map(renderNavItem)}
              {categoryItems.map(renderNavItem)}
            </Accordion>
          </nav>
          {showSheetTheme || showLanguage ? (
            <div className="mt-4 border-t border-border px-3 pt-4 flex flex-col gap-2">
              {showSheetTheme ? <ModoEscuroToggleSheet /> : null}
              {showLanguage && language ? (
                <LanguageFlagToggleSheet
                  currentLocale={language.currentLocale}
                  defaultLocale={language.defaultLocale}
                  languages={language.languages}
                  showFlags={language.showFlags}
                />
              ) : null}
            </div>
          ) : null}
          {login.enabled || signup.enabled ? (
            <nav
              className="mt-auto flex flex-col gap-2 border-t border-border pt-4"
              aria-label={t('accountAccess')}
            >
              {login.enabled ? (
                <Button variant="outline" size="sm" className="w-full font-semibold" asChild>
                  <LocaleLink href={login.href}>{login.label}</LocaleLink>
                </Button>
              ) : null}
              {signup.enabled ? (
                <Button size="sm" className="w-full font-semibold" asChild>
                  <LocaleLink href={signup.href}>{signup.label}</LocaleLink>
                </Button>
              ) : null}
            </nav>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}
